/**
 * `/admin/members/{id}/health-policy` - spec 22, the member's Health &
 * Accident policy.
 *
 * The file is an ImageKit PRIVATE file. `file_url` is a signed link that stops
 * working at `url_expires_at` (15 minutes by default), so it is never stored or
 * copied anywhere: the screen asks again when the link is stale.
 */

import { request } from "./client";

export type HealthPolicy = {
  file_kind: "pdf" | "image";
  file_url: string;
  url_expires_at: string;
  policy_number: string | null;
  insurer: string | null;
  /** ISO date. */
  valid_until: string | null;
  updated_at: string;
};

export type HealthPolicyUpdate = {
  policy_number?: string | null;
  insurer?: string | null;
  valid_until?: string | null;
};

type PolicyResult = { policy: HealthPolicy | null };
type PolicyWriteResult = PolicyResult & { message: string };

export function fetchHealthPolicy(memberId: string): Promise<PolicyResult> {
  return request(`/admin/members/${memberId}/health-policy`);
}

/**
 * Multipart field `file`: a PDF, JPEG, PNG or WebP up to 10 MB. The API checks
 * the type from the file's bytes, so the browser's guess is not trusted.
 */
export function uploadHealthPolicyFile(memberId: string, file: File): Promise<PolicyWriteResult> {
  const body = new FormData();
  body.append("file", file);
  return request(`/admin/members/${memberId}/health-policy/file`, { method: "PUT", body });
}

/** Only the fields sent change; `null` clears one. 404 until a file exists. */
export function updateHealthPolicy(memberId: string, update: HealthPolicyUpdate): Promise<PolicyWriteResult> {
  return request(`/admin/members/${memberId}/health-policy`, { method: "PATCH", body: update });
}

export function removeHealthPolicy(memberId: string): Promise<PolicyWriteResult> {
  return request(`/admin/members/${memberId}/health-policy`, { method: "DELETE" });
}
